import * as React from 'react'

import { Button } from '../../../lib/ui/button'
import { Calendar } from '../../../lib/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../lib/ui/popover'
import { CircleX } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useSavedJobs } from './SavedJobsProvider'
import { getDatePhase } from './getDatePhase'
import { useTranslation } from '../../../hooks/useTranslation'
import { useFormatTime } from '../../../hooks/useFormatTime'
import moment from 'moment'

export function FollowupButton({ savedJob, rowIndex, variant }) {
  const { t } = useTranslation('FollowupButton')
  const { locale } = useTranslation()

  const { patchSavedJob } = useSavedJobs()
  const [calendarOpen, setCalendarOpen] = React.useState(false)
  const { followup_on } = savedJob
  const queryClient = useQueryClient()

  const updateData = (rowIndex, columnId, value) => {
    queryClient.setQueryData(['get-saved-jobs'], (old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          }
        }
        return row
      }),
    )
  }

  const mutation = useMutation({
    mutationFn: (updates) => patchSavedJob(savedJob, updates),
    onSettled: () => {
      queryClient.invalidateQueries(['get-saved-jobs'])
    },
  })

  const handleSelectDate = (date) => {
    updateData(rowIndex, 'followup_on', date || undefined)
    updateData(rowIndex, 'updated_at', new Date())
    mutation.mutate({
      followup_on: date ? format(date, 'yyyy-MM-dd') : null,
    })
    setCalendarOpen(false)
  }

  const handleSetNoDate = () => {
    updateData(rowIndex, 'followup_on', undefined)
    updateData(rowIndex, 'updated_at', new Date())
    mutation.mutate({ followup_on: null })
  }

  const datePhase = getDatePhase(followup_on)

  const formatTime = useFormatTime()
  const date = followup_on
  const formattedDate = formatTime(moment(date), 'short', locale)

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <PopoverTrigger>
        <div
          className="justify-start rounded-xl border border-solid border-gray-300 bg-transparent p-2 text-center text-base font-normal text-black hover:bg-accent hover:text-accent-foreground data-[phase=future]:bg-transparent data-[phase=none]:bg-transparent data-[phase=overdue]:bg-[#FC7C5E] data-[phase=soon]:bg-[#FFE769] data-[phase=none]:text-muted-foreground"
          data-phase={datePhase}
        >
          {followup_on ? formattedDate : <EnterDate variant={variant} />}
        </div>
      </PopoverTrigger>
      {followup_on && (
        <Button
          variant="ghost"
          className="ml-1 items-center p-1 text-muted-foreground hover:text-black"
          onClick={handleSetNoDate}
        >
          <span className="sr-only">{t('clear_date')}</span>
          <CircleX size={14} />
        </Button>
      )}
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={new Date(followup_on)}
          onSelect={handleSelectDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

function EnterDate({ variant }) {
  const { t } = useTranslation('FollowupButton')

  if (variant !== 'outline') {
    return <>{t('enter_date')}</>
  }

  return (
    <div className="flex h-full w-full items-center justify-center rounded-xl text-sm font-normal text-black">
      {t('enter_date')}
    </div>
  )
}
